import React, { useContext, useMemo, useState } from "react";
import { Plus, Check, X } from "lucide-react";
import CustomizerContext from "../../../_helper/Customizer";
import { useTicketsOptional } from "../../../_helper/Tickets/TicketsProvider";
import TicketListTab from "./TicketListTab";
import TicketCardGrid from "./TicketCardGrid";
import TicketTemplatesTab from "./TicketTemplatesTab";
import DynamicModalForm from "../../Modal/DynamicModalForm";
import { useQuickAddRegistrar } from "../../../_helper/QuickAdd/QuickAddProvider";
import {
  TableToolbar,
  ViewSwitcher,
  IconButton,
  ListPageShell,
  useViewMode,
} from "../../../design-system";

const projectOptionsDefault = [
  "EuroDrive Cyprus Mayıs’25 Web Teknik Seo/Yazılım Çalışması",
  "Bade Natural Performans Pazarlama",
  "Hotel Pia Bella Kreatif",
];

const typeOptionsDefault = [
  "Yazılım ve Teknik Destek",
  "Performans Pazarlama",
  "Kreatif",
];

const assigneeOptionsDefault = ["Uğur AKÇIL", "Aslı Hoca", "Ozan Çakmak"];

const labelOptionsDefault = ["Acil", "Bug", "SEO", "UI"];

export const ticketFields = [
  { type: "text", label: "Title", name: "title", placeholder: "Title" },
  {
    type: "select",
    label: "Client",
    name: "client",
    options: [
      { value: "-", label: "-" },
      { value: "Bade Natural", label: "Bade Natural" },
      { value: "Hotel Pia Bella", label: "Hotel Pia Bella" },
      { value: "Riverside Garden Resort", label: "Riverside Garden Resort" },
    ],
  },
  {
    type: "select",
    label: "Requested by",
    name: "requestedBy",
    options: [
      { value: "-", label: "-" },
      { value: "Müşteri", label: "Müşteri" },
      { value: "Ekip Üyesi", label: "Ekip Üyesi" },
    ],
  },
  {
    type: "select",
    label: "Project",
    name: "project",
    options: [
      { value: "Project", label: "Project" },
      {
        value: "Bade Natural Performans Pazarlama Çalışması Mayıs’25",
        label: "Bade Natural Performans Pazarlama Çalışması Mayıs’25",
      },
    ],
  },
  {
    type: "select",
    label: "Ticket type",
    name: "type",
    options: [
      {
        value: "E-İhracat ve Global Pazarlama",
        label: "E-İhracat ve Global Pazarlama",
      },
      {
        value: "Muhasebe ve Faturalandırma",
        label: "Muhasebe ve Faturalandırma",
      },
      {
        value: "Tasarım ve Kreatif Çözümler",
        label: "Tasarım ve Kreatif Çözümler",
      },
    ],
  },
  {
    type: "textarea",
    label: "Description",
    name: "description",
    placeholder: "Description",
  },
  {
    type: "select",
    label: "Assign to",
    name: "assignTo",
    options: [
      { value: "-", label: "-" },
      { value: "Ali Yılmaz", label: "Ali Yılmaz" },
      { value: "Aslı Hoca", label: "Aslı Hoca" },
      { value: "Ozan Çakmak", label: "Ozan Çakmak" },
    ],
  },
  {
    type: "multiselect",
    label: "Labels",
    name: "labels",
    options: [
      { value: "Acil", label: "Acil" },
      { value: "İçerik", label: "İçerik" },
      { value: "Görsel", label: "Görsel" },
      { value: "SEO", label: "SEO" },
    ],
  },
  { type: "file", label: "Upload File", name: "file" },
];

const editTicketFields = (
  projects = projectOptionsDefault,
  types = typeOptionsDefault,
  assignees = assigneeOptionsDefault,
  labels = labelOptionsDefault
) => [
  { type: "text", label: "Title", name: "title", placeholder: "Title" },
  {
    type: "select",
    label: "Project",
    name: "project",
    options: projects.map((p) => ({ value: p, label: p })),
  },
  {
    type: "select",
    label: "Ticket type",
    name: "type",
    options: types.map((p) => ({ value: p, label: p })),
  },
  {
    type: "select",
    label: "Assign to",
    name: "assigned",
    options: assignees.map((p) => ({ value: p, label: p })),
  },
  {
    type: "multiselect",
    label: "Labels",
    name: "labels",
    options: labels.map((p) => ({ value: p, label: p })),
  },
];

const templateFields = [
  { type: "text", label: "Title", name: "title", placeholder: "Title" },
  {
    type: "textarea",
    label: "Description",
    name: "description",
    placeholder: "Description",
  },
  {
    type: "select",
    label: "Ticket type",
    name: "type",
    options: [
      { value: "-", label: "-" },
      { value: "Performans Pazarlama", label: "Performans Pazarlama" },
      { value: "Medya Planlama", label: "Medya Planlama" },
      { value: "Raporlama", label: "Raporlama" },
      { value: "Kreatif", label: "Kreatif" },
    ],
  },
  { type: "checkbox", label: "Private", name: "private" },
];

const TicketPage = ({
  initialTickets = [],
  initialTemplates = [],
  showTemplatesTab = true,
  showTabs = true,
  actions = {},
  title = "Tickets",
  layoutURL: layoutURLProp,
  listProps = {},
  variant = "default",
  scope,
  entityId,
}) => {
  const ticketsCtx = useTicketsOptional();
  const mergedActions = {
    showAddTicket: true,
    showAddTemplate: true,
    ...actions,
  };

  const scopedEnabled = !!(scope && entityId);

  const [view, setView] = useState("ticket-list");
  const [viewMode, setViewMode] = useViewMode("tickets", "list-1");
  const showCardGrid = viewMode !== "list-1";
  const [localTickets, setLocalTickets] = useState(initialTickets);
  const [localTemplates, setLocalTemplates] = useState(initialTemplates);

  const tickets =
    (scopedEnabled && ticketsCtx?.getTicketsFor?.(scope, entityId)) ||
    ticketsCtx?.tickets ||
    localTickets;
  const setTickets =
    (scopedEnabled && ticketsCtx?.setTicketsFor
      ? (updater) => ticketsCtx.setTicketsFor(scope, entityId, updater)
      : ticketsCtx?.setTickets) || setLocalTickets;
  const templates = ticketsCtx?.templates ?? localTemplates;
  const setTemplates = ticketsCtx?.setTemplates ?? setLocalTemplates;

  const [showTicketModal, setShowTicketModal] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [currentTicket, setCurrentTicket] = useState(null);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [batchMode, setBatchMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());

  useQuickAddRegistrar(
    view === "ticket-list" && mergedActions.showAddTicket
      ? {
          addTicket: () => {
            setCurrentTicket(null);
            setShowTicketModal(true);
          },
        }
      : {},
    [view, mergedActions.showAddTicket]
  );

  const { layoutURL: ctxLayout } = useContext(CustomizerContext);
  const layoutURL = layoutURLProp || ctxLayout;

  const handleAddTicket = (data) => {
    if (ticketsCtx?.addTicket) return ticketsCtx.addTicket(data, scope, entityId);
    const newTicket = {
      id: Math.floor(Math.random() * 10000).toString(),
      title: data.title || "-",
      client: data.client || "-",
      project: data.project || "-",
      type: data.type || "-",
      assigned: data.assigned || data.assignTo || "-",
      activity: "Just now",
      status: "New",
    };
    setTickets((prev) => {
      const nextList = [...prev, newTicket];
      return nextList;
    });
    return newTicket;
  };

  const handleEditTicket = (updated) => {
    if (ticketsCtx?.updateTicket)
      return ticketsCtx.updateTicket(updated, scope, entityId);
    setTickets((prev) => prev.map((t) => (t.id === updated.id ? updated : t)));
  };

  const handleDeleteTicket = (id) => {
    if (!id) return;
    if (ticketsCtx?.deleteTicket) return ticketsCtx.deleteTicket(id, scope, entityId);
    setTickets((prev) => prev.filter((t) => t.id !== id));
  };

  const handleToggleStatusBulk = () => {
    if (selectedIds.size === 0) return;
    setTickets((prev) =>
      prev.map((t) => {
        if (!selectedIds.has(t.id)) return t;
        const nextStatus = t.status === "Closed" ? "Open" : "Closed";
        return { ...t, status: nextStatus };
      })
    );
    setSelectedIds(new Set());
    setBatchMode(false);
  };

  const handleSaveTemplate = (tpl) => {
    if (ticketsCtx?.saveTemplate) return ticketsCtx.saveTemplate(tpl);
    if (tpl.id) {
      setTemplates((prev) =>
        prev.map((t) => (t.id === tpl.id ? { ...t, ...tpl } : t))
      );
    } else {
      const newTpl = { ...tpl, id: Date.now().toString() };
      setTemplates((prev) => [newTpl, ...prev]);
    }
  };

  const tabs = useMemo(() => {
    const base = [
      {
        key: "ticket-list",
        label: "Ticket List",
        component: (
          <>
            {!showCardGrid && (
              <TicketListTab
                tickets={tickets}
                setTickets={setTickets}
                layoutURL={layoutURL}
                onEdit={(t) => {
                  setCurrentTicket(t);
                  setShowTicketModal(true);
                }}
                onDelete={handleDeleteTicket}
                batchUpdate={batchMode}
                selectedIds={selectedIds}
                onToggleRow={(id) => {
                  setSelectedIds((prev) => {
                    const next = new Set(prev);
                    next.has(id) ? next.delete(id) : next.add(id);
                    return next;
                  });
                }}
                onClearSelection={() => setSelectedIds(new Set())}
                {...listProps}
              />
            )}
            {showCardGrid && (
              <TicketCardGrid
                tickets={tickets}
                viewMode={viewMode}
                onOpen={(t) => {
                  setCurrentTicket(t);
                  setShowTicketModal(true);
                }}
              />
            )}
          </>
        ),
      },
    ];
    if (showTemplatesTab) {
      base.push({
        key: "ticket-templates",
        label: "Ticket Templates",
        component: (
          <TicketTemplatesTab
            templates={templates}
            setTemplates={setTemplates}
            onRequestEdit={(tpl) => {
              setEditingTemplate(tpl);
              setShowTemplateModal(true);
            }}
            onDelete={(id) => {
              if (ticketsCtx?.deleteTemplate)
                return ticketsCtx.deleteTemplate(id);
              return setTemplates((prev) => prev.filter((t) => t.id !== id));
            }}
            onAddTemplate={() => {
              setEditingTemplate(null);
              setShowTemplateModal(true);
            }}
          />
        ),
      });
    }
    return base;
  }, [
    layoutURL,
    listProps,
    showTemplatesTab,
    templates,
    tickets,
    ticketsCtx,
    showCardGrid,
    viewMode,
    batchMode,
    selectedIds,
  ]);

  // ensure view valid
  useMemo(() => {
    if (!tabs.find((t) => t.key === view))
      setView(tabs[0]?.key || "ticket-list");
  }, [tabs, view]);

  return (
    <ListPageShell>
      <TableToolbar
        title={title}
        tabs={
          showTabs && tabs.length > 1
            ? tabs.map((t) => ({ value: t.key, label: t.label }))
            : null
        }
        activeTab={view}
        onTabChange={setView}
        viewSlot={
          view === "ticket-list" ? (
            <ViewSwitcher value={viewMode} onChange={setViewMode} />
          ) : null
        }
        actions={
          <>
            {view === "ticket-list" && (
              <IconButton
                variant={batchMode ? "solid" : "outlined"}
                size="md"
                ariaLabel="Batch update"
                onClick={() => {
                  if (batchMode) handleToggleStatusBulk();
                  else setBatchMode(true);
                }}
              >
                <Check size={16} />
              </IconButton>
            )}
            {batchMode && selectedIds.size > 0 && (
              <IconButton
                variant="outlined"
                size="md"
                ariaLabel="Cancel selection"
                onClick={() => {
                  setSelectedIds(new Set());
                  setBatchMode(false);
                }}
              >
                <X size={16} />
              </IconButton>
            )}
            {view === "ticket-list" && mergedActions.showAddTicket && (
              <IconButton
                variant="solid"
                size="md"
                ariaLabel="Add ticket"
                onClick={() => {
                  setCurrentTicket(null);
                  setShowTicketModal(true);
                }}
              >
                <Plus size={18} />
              </IconButton>
            )}
            {showTemplatesTab &&
              mergedActions.showAddTemplate &&
              view === "ticket-templates" && (
                <IconButton
                  variant="solid"
                  size="md"
                  ariaLabel="Add template"
                  onClick={() => {
                    setEditingTemplate(null);
                    setShowTemplateModal(true);
                  }}
                >
                  <Plus size={18} />
                </IconButton>
              )}
          </>
        }
      />

      <ListPageShell.Content
        data-mode={
          view === "ticket-list" && showCardGrid ? "card" : "list"
        }
      >
        {showTabs ? (
          tabs.find((t) => t.key === view)?.component
        ) : (
          tabs[0]?.component
        )}
      </ListPageShell.Content>

      {mergedActions.showAddTicket && (
        <DynamicModalForm
          isOpen={showTicketModal}
          toggle={() => {
            setShowTicketModal(false);
            setCurrentTicket(null);
          }}
          title={currentTicket ? "Edit Ticket" : "Add Ticket"}
          formData={currentTicket || {}}
          setFormData={(data) => setCurrentTicket(data)}
          onSave={(data) => {
            currentTicket ? handleEditTicket(data) : handleAddTicket(data);
            setShowTicketModal(false);
            setCurrentTicket(null);
          }}
          fields={
            currentTicket
              ? editTicketFields(
                  listProps.projectOptions || projectOptionsDefault,
                  listProps.typeOptions || typeOptionsDefault,
                  listProps.assigneeOptions || assigneeOptionsDefault,
                  listProps.labelOptions || labelOptionsDefault
                )
              : ticketFields
          }
        />
      )}

      {showTemplatesTab && mergedActions.showAddTemplate && (
        <DynamicModalForm
          isOpen={showTemplateModal}
          toggle={() => {
            setShowTemplateModal(false);
            setEditingTemplate(null);
          }}
          title={editingTemplate ? "Edit Template" : "Add Template"}
          formData={editingTemplate || {}}
          setFormData={(data) => setEditingTemplate(data)}
          onSave={(data) => {
            handleSaveTemplate(data);
            setShowTemplateModal(false);
            setEditingTemplate(null);
          }}
          fields={templateFields}
        />
      )}
    </ListPageShell>
  );
};

export default TicketPage;
